import React, { useCallback, useMemo } from 'react';
import GridusTitle from './GridusTitle';
import MenuItem from './MenuItem';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDecodedIdToken } from '@/contexts/DecodeIdTokenContext';
import { ToggleOperation } from './ToggleOperation';
import SubmitButton from '../common/SubmitButton';
import { clearToken } from '@/utils/cookie';

enum UserRole {
  User,
  Admin,
  SuperAdmin,
}

function Sidebar() {
  const userAttributes = useDecodedIdToken();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const mainLinks = [
    {
      path: '/forecast',
      label: t('sidebar.forecast'),
      icon: 'forecast',
      isActive: true,
    },
    {
      path: '/overview',
      label: t('sidebar.overview'),
      icon: 'site',
      isActive: true,
    },
    // {
    //   path: '/invoice',
    //   label: t('sidebar.settlement'),
    //   icon: 'coin',
    //   isActive: true,
    // },
    // {
    //   path: '/auction',
    //   label: t('sidebar.auction'),
    //   icon: 'bid',
    //   isActive: false,
    // },
    {
      path: '/account-management',
      label: t('sidebar.accountManagement'),
      icon: 'account',
      isActive: true,
    },
    // {
    //   path: '/notice',
    //   label: t('sidebar.notice'),
    //   icon: 'notice',
    //   isActive: false,
    // },
  ];

  const operationLinks = [
    // {
    //   path: '/operation/prediction',
    //   label: t('sidebar.prediction', { context: 'operation' }),
    //   icon: 'prediction',
    //   isActive: false,
    // },
    // {
    //   path: '/operation/auction',
    //   label: t('sidebar.auction', { context: 'operation' }),
    //   icon: 'bid',
    //   isActive: false,
    // },
    {
      path: '/operation/settlement',
      label: t('sidebar.settlement', { context: 'operation' }),
      icon: 'coin',
      isActive: true,
    },
    {
      path: '/operation/management',
      label: t('sidebar.management', { context: 'operation' }),
      icon: 'management',
      isActive: true,
    },
    {
      path: '/operation/analysis',
      label: t('sidebar.analysis', { context: 'operation' }),
      icon: 'analysis',
      isActive: true,
    },
    // {
    //   path: '/operation/customerService',
    //   label: t('sidebar.customerService', { context: 'operation' }),
    //   icon: 'customer-service',
    //   isActive: false,
    // },
  ];

  const extraLinks = [
    // {
    //   path: '/setting',
    //   label: t('sidebar.setting'),
    //   icon: 'setting',
    //   isActive: false,
    // },
  ] as Array<{ path: string; label: string; icon: string; isActive: boolean }>;

  const isSA = useMemo(() => {
    const isSA =
      userAttributes?.['custom:role'] === UserRole.SuperAdmin.toString();
    return isSA;
  }, [userAttributes]);

  const isOperation = useMemo(() => {
    const isOperation = !!location.pathname.includes('operation');
    return isOperation;
  }, [location.pathname]);

  const showExtraMenu = !isOperation;

  const handleOperationClick = useCallback(() => {
    if (isOperation) navigate('/forecast');
    else navigate('/operation/settlement');
  }, [isOperation, navigate]);

  const navClassName = isOperation ? 'bg-dark-2' : 'bg-white';

  const handleLogOut = () => {
    clearToken('accessToken');
    clearToken('idToken');
    clearToken('refreshToken');
    navigate('/');
  };

  return (
    <nav
      data-testid="sidebar-nav"
      className={`sticky left-0 top-0 h-[100vh] w-[240px] min-w-[240px] whitespace-nowrap ${navClassName} flex flex-col shadow-1`}
    >
      {/* title */}
      <div className="flex items-center justify-center pt-[40px]">
        <GridusTitle
          textFill={isOperation ? '#ffffff' : '#272C2D'}
        ></GridusTitle>
      </div>
      {/* toggleOperation */}
      {isSA && (
        <div className="mt-[30px] flex items-center justify-center">
          <ToggleOperation
            isOperation={isOperation}
            onClick={handleOperationClick}
            testId="sidebar-toggle-operation"
            className="cursor-pointer"
          />
        </div>
      )}
      {/* menus */}
      <div
        className={`mb-[55px] ${isSA ? 'mt-[30px]' : 'mt-[55px]'} px-[20px]`}
      >
        {(isOperation && isSA ? operationLinks : mainLinks).map(
          ({ path, label, icon, isActive }, index) => {
            const isSelected = location.pathname === path;
            const className = index === 0 ? '' : 'mt-[10px]';

            return (
              <MenuItem
                isOperation={isOperation}
                key={path}
                link={path}
                text={label}
                className={className}
                icon={icon}
                isSelected={isSelected}
                isActive={isActive}
              ></MenuItem>
            );
          }
        )}
      </div>
      {showExtraMenu && extraLinks.length ? (
        <div className="h-[0.5px] bg-light-3"></div>
      ) : (
        <></>
      )}
      {showExtraMenu && (
        <div className="mt-[54px] px-[20px]">
          {extraLinks.map(({ path, label, icon, isActive }, index) => {
            const isSelected = location.pathname === path;
            const className = index === 0 ? '' : 'mt-[5px]';

            return (
              <MenuItem
                isOperation={isOperation}
                key={path}
                link={path}
                text={label}
                className={className}
                icon={icon}
                isSelected={isSelected}
                isActive={isActive}
              ></MenuItem>
            );
          })}
        </div>
      )}
      <div className="mb-5 mt-auto flex flex-col items-center justify-center">
        {userAttributes?.['email'] && (
          <>
            <p
              className={`pop-menu-normal ${isOperation ? 'text-[#ffffff]' : 'text-[#272C2D]'}`}
            >
              현재 로그인 정보{' '}
            </p>
            <p
              className={`pop-menu-normal ${isOperation ? 'text-[#ffffff]' : 'text-[#272C2D]'} mb-2`}
            >
              {userAttributes?.['email']}
            </p>
          </>
        )}
        <SubmitButton text="로그아웃" onClick={handleLogOut} disabled={false} />
      </div>
    </nav>
  );
}

export default Sidebar;
